/**
 * Reprint documents: order number, then one press per document.
 *
 * The call is the Orders drawer's reprint, through `reprintMany`, so the bridge command id
 * rules and the "already queued" replay are the same here as in the drawer.
 */
import { useState } from 'react';
import type { PrintDoc } from '@elutide/shared';
import { Button, Card, useToast } from '@/components';
import { reprintMany } from '@/features/orders/api';
import { OrderFinder, OrderSummary } from './OrderFinder';
import type { BenchOrderRow } from './types';

const CHOICES: readonly { id: string; label: string; docs: readonly PrintDoc[] }[] = [
  { id: 'slip', label: 'Packing slip', docs: ['packing_slip'] },
  { id: 'label', label: 'Shipping label', docs: ['label'] },
  { id: 'coa', label: 'Certificate', docs: ['coa'] },
  { id: 'all', label: 'All three', docs: ['packing_slip', 'coa', 'label'] },
];

export function ReprintDocuments() {
  const toast = useToast();
  const [order, setOrder] = useState<BenchOrderRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function reprint(choice: (typeof CHOICES)[number]): Promise<void> {
    if (!order) return;
    setBusy(choice.id);
    setProblem(null);
    const [result] = await reprintMany([order.order_id], choice.docs);
    setBusy(null);
    if (!result) return;
    if (result.ok) {
      toast.push({
        tone: 'ok',
        message: result.replayed
          ? 'That reprint was already on its way.'
          : `Sent ${choice.label.toLowerCase()} for #${order.order_number} to the printer.`,
      });
    } else {
      setProblem(result.error ?? 'The reprint did not go through.');
    }
  }

  return (
    <Card title="Reprint documents" id="bench-reprint">
      <p className="o-muted bench-lead">
        Type the order number from the slip or the box, then choose what to print again.
      </p>
      <OrderFinder idPrefix="reprint" onFound={setOrder} onCleared={() => setOrder(null)} />

      {order ? (
        <div className="bench-actions">
          <OrderSummary order={order} />
          {problem ? (
            <p className="bench-problem" role="alert">
              {problem}
            </p>
          ) : null}
          <div className="bench-actions__row">
            {CHOICES.map((choice) => (
              <Button
                key={choice.id}
                variant={choice.id === 'all' ? 'primary' : 'default'}
                busy={busy === choice.id}
                disabled={busy !== null}
                onClick={() => void reprint(choice)}
              >
                {choice.label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  );
}
